import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  Check,
  Search,
  Loader2,
  GripVertical,
  Plus,
  Pencil,
  X,
  Info,
} from "lucide-react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { getSectionDefinition, type ContentType } from "./homepageConfig";

import { useLazyGetCategoryQuery } from "../../../redux/actions/Admin/dashboardAdmin";

import {
  useLazyGetAdminProductsQuery,
  useLazyGetAllBrandsQuery,
  useLazyGetSectionItemsQuery,
  useLazyGetAvailableVideosQuery,
  useUpdateSectionItemMutation,
} from "../../../redux/actions/Admin/ProductAdmin";

import { useToast } from "../../../utils/useToast";
import ToastContainer from "../../../utils/ToastContainer";
import { useLazyGetAllPetsQuery } from "../../../redux/actions/Admin/PetsAdmin";

// ============================================================
// TYPES
// ============================================================

type SourceType = "Manual" | "Dynamic";

type VideoItemType = "Product" | "Pet";

interface VideoSuggestion {
  itemId: number;
  itemType: VideoItemType;
  name: string;
  imageUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl: string | null;
}

interface VideoSuggestionsResponse {
  sectionType: string;
  items: VideoSuggestion[];
}

type SelectedItem = {
  clientKey: string;
  id: number;
  itemType: ContentType;
  itemId: number | null;
  name: string;
  imageUrl?: string | null;
  displayOrder: number;
  isActive: boolean;

  title?: string | null;
  redirectUrl?: string | null;
  configJson?: string | null;
};

type CategoryNode = {
  id: number;
  name: string;
  level?: number;
  imageUrl?: string | null;
  isActive?: boolean;
  isDeleted?: boolean;
  parentId?: number | null;
  children?: CategoryNode[];
};

type CustomForm = {
  title: string;
  imageUrl: string;
  redirectUrl: string;
  configJson: string;
};

// ============================================================
// COMPONENT
// ============================================================

const ManageSectionItemsScreen: React.FC = () => {
  const navigate = useNavigate();

  const { sectionId } = useParams();

  const location = useLocation();

  // ----------------------------------------------------------
  // Navigation state
  // ----------------------------------------------------------

  const state = location.state as
    | {
        section?: any;
        screenId?: number;
        screenName?: string;
      }
    | undefined;

  const section = state?.section;

  const id = Number(sectionId);

  console.log(id, "sectionId", section, "Section");

  // ============================================================
  // SECTION DEFINITION
  // ============================================================

  const definition = getSectionDefinition(
    section?.sectionType || "",
    state?.screenName,
  );

  // ============================================================
  // API
  // ============================================================

  const [
    getSectionItems,
    { data: existingItems = [], isLoading: isLoadingItems },
  ] = useLazyGetSectionItemsQuery();

  const [
    getAdminProducts,
    {
      data: productsResponse,
      isLoading: isLoadingProducts,
      isFetching: isFetchingProducts,
    },
  ] = useLazyGetAdminProductsQuery();

  const [
    getCategoryTree,
    {
      data: categoryResponse,
      isLoading: isLoadingCategories,
      isFetching: isFetchingCategories,
    },
  ] = useLazyGetCategoryQuery();

  const [
    getBrands,
    {
      data: brands = [],
      isLoading: isLoadingBrands,
      isFetching: isFetchingBrands,
    },
  ] = useLazyGetAllBrandsQuery();

  const [
    getAvailableVideos,
    {
      data: videosResponse,
      isLoading: isLoadingVideos,
      isFetching: isFetchingVideos,
    },
  ] = useLazyGetAvailableVideosQuery();

  const [
  getPets,
  {
    data: petsResponse,
    isLoading: isLoadingPets,
    isFetching: isFetchingPets,
  },
] = useLazyGetAllPetsQuery();

  const [saveSectionItems, { isLoading: isSaving }] =
    useUpdateSectionItemMutation();

  // ============================================================
  // STATE
  // ============================================================

  const [search, setSearch] = useState("");

  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const [editingCustomKey, setEditingCustomKey] =
    useState<string | null>(null);

  const [customForm, setCustomForm] = useState<CustomForm>({
    title: "",
    imageUrl: "",
    redirectUrl: "",
    configJson: "",
  });

  const { toasts, addToast, removeToast } = useToast();

  // ============================================================
  // RESOLVE CONTENT TYPE
  // ============================================================

  const contentType = useMemo<ContentType | undefined>(() => {
    const rawContentType =
      section?.contentType ?? section?.ContentType;

    // ----------------------------------------------------------
    // Numeric enum returned by backend
    // ----------------------------------------------------------

    if (typeof rawContentType === "number") {
      const contentTypeMap: Record<number, ContentType> = {
        1: "Product",
        2: "Category",
        3: "Brand",
        4: "Pet",
        5: "Service",
        6: "Video",
        7: "Custom",
      };

      return (
        contentTypeMap[rawContentType] ??
        definition?.itemType
      );
    }

    // ----------------------------------------------------------
    // String enum returned by backend
    // ----------------------------------------------------------

    if (typeof rawContentType === "string") {
      const normalized = rawContentType.trim();

      const validContentTypes: ContentType[] = [
        "Product",
        "Category",
        "Brand",
        "Pet",
        "Service",
        "Video",
        "Custom",
      ];

      if (
        validContentTypes.includes(
          normalized as ContentType,
        )
      ) {
        return normalized as ContentType;
      }
    }

    // ----------------------------------------------------------
    // Fallback
    // ----------------------------------------------------------

    return definition?.itemType;
  }, [
    section?.contentType,
    section?.ContentType,
    definition?.itemType,
  ]);

  // ============================================================
  // RESOLVE SOURCE TYPE
  // ============================================================

  const sourceType = useMemo<SourceType>(() => {
    const rawConfig =
      section?.configJson ??
      section?.ConfigJson ??
      section?.config ??
      null;

    if (!rawConfig) {
      return "Manual";
    }

    let config: any = rawConfig;

    // Config is already an object
    if (
      typeof config === "object" &&
      config !== null
    ) {
      const value =
        config.SourceType ??
        config.sourceType;

      return String(value).toLowerCase() ===
        "dynamic"
        ? "Dynamic"
        : "Manual";
    }

    // Config is JSON string
    if (typeof config === "string") {
      try {
        const parsed = JSON.parse(config);

        const value =
          parsed?.SourceType ??
          parsed?.sourceType;

        return String(value).toLowerCase() ===
          "dynamic"
          ? "Dynamic"
          : "Manual";
      } catch {
        return "Manual";
      }
    }

    return "Manual";
  }, [
    section?.configJson,
    section?.ConfigJson,
    section?.config,
  ]);

  // ============================================================
  // WHETHER ADMIN CAN MANAGE CONTENT
  // ============================================================

  const isDynamicSection =
    contentType !== "Custom" &&
    sourceType === "Dynamic";

  const canManageContent =
    contentType === "Custom" ||
    !isDynamicSection;

  // ============================================================
  // LOAD EXISTING SECTION ITEMS
  // ============================================================

  const loadExisting = useCallback(async () => {
    if (!id) {
      return;
    }

    if (isDynamicSection) {
      setSelectedItems([]);
      return;
    }

    try {
      const result =
        await getSectionItems(id).unwrap();

      const sorted = [...result].sort(
        (a: any, b: any) =>
          Number(a.displayOrder || 0) -
          Number(b.displayOrder || 0),
      );

      const normalized: SelectedItem[] =
        sorted.map((item: any) => ({
          clientKey: `existing-${item.id}`,

          id: Number(item.id || 0),

          itemType:
            item.itemType as ContentType,

          itemId:
            item.itemId == null
              ? null
              : Number(item.itemId),

          name:
            item.name ||
            item.title ||
            `${item.itemType} #${
              item.itemId ?? ""
            }`,

          imageUrl:
            item.imageUrl || null,

          displayOrder:
            Number(item.displayOrder || 0),

          isActive:
            item.isActive !== false,

          title:
            item.title ?? null,

          redirectUrl:
            item.redirectUrl ?? null,

          configJson:
            item.configJson ?? null,
        }));

      setSelectedItems(normalized);
    } catch (error) {
      console.error(
        "Failed to load section items",
        error,
      );

      setSelectedItems([]);
    }
  }, [
    id,
    getSectionItems,
    isDynamicSection,
  ]);

  useEffect(() => {
    loadExisting();
  }, [loadExisting]);

  // ============================================================
  // LOAD AVAILABLE DATA FROM DATABASE
  // ============================================================
useEffect(() => {
  if (!contentType) {
    return;
  }

  if (isDynamicSection) {
    return;
  }

  // ----------------------------------------------------------
  // PET
  // ----------------------------------------------------------

  if (contentType === "Pet") {
    getPets({});
    return;
  }

  // ----------------------------------------------------------
  // VIDEO
  // ----------------------------------------------------------

  if (contentType === "Video") {
    const sectionType = section?.sectionType;

    if (
      sectionType === "VideoBanners" ||
      sectionType === "VideoCarouselPets"
    ) {
      getAvailableVideos({
        sectionType,
        limit: 50,
      });
    }

    return;
  }

  // CATEGORY
  if (contentType === "Category") {
    getCategoryTree();
    return;
  }

  // BRAND
  if (contentType === "Brand") {
    getBrands();
    return;
  }
}, [
  contentType,
  isDynamicSection,
  section?.sectionType,
  getPets,
  getAvailableVideos,
  getCategoryTree,
  getBrands,
]);

  // ============================================================
  // PRODUCT SEARCH
  // ============================================================

  useEffect(() => {
    if (
      contentType !== "Product" ||
      isDynamicSection
    ) {
      return;
    }

    getAdminProducts({
      searchText:
        search.trim() || undefined,

      pageNumber: 1,

      pageSize: 50,

      isActive: true,

      isPublished: true,
    });
  }, [
    contentType,
    isDynamicSection,
    search,
    getAdminProducts,
  ]);

  // ============================================================
  // CATEGORY NORMALIZATION
  // ============================================================

  const normalizeCategoryResponse =
    useMemo(() => {
      if (!categoryResponse) {
        return [];
      }

      if (Array.isArray(categoryResponse)) {
        return categoryResponse as CategoryNode[];
      }

      if (
        Array.isArray(
          (categoryResponse as any).data,
        )
      ) {
        return (categoryResponse as any)
          .data as CategoryNode[];
      }

      if (
        Array.isArray(
          (categoryResponse as any).items,
        )
      ) {
        return (categoryResponse as any)
          .items as CategoryNode[];
      }

      if (
        Array.isArray(
          (categoryResponse as any).categories,
        )
      ) {
        return (categoryResponse as any)
          .categories as CategoryNode[];
      }

      return [];
    }, [categoryResponse]);
const filteredPets = useMemo(() => {
  if (!Array.isArray(petsResponse)) {
    return [];
  }

  const value = search.trim().toLowerCase();

  if (!value) {
    return petsResponse;
  }

  return petsResponse.filter((pet: any) => {
    return (
      String(pet.petName || "")
        .toLowerCase()
        .includes(value) ||
      String(pet.breed || "")
        .toLowerCase()
        .includes(value) ||
      String(pet.microchipId || "")
        .toLowerCase()
        .includes(value) ||
      String(pet.petCode || "")
        .toLowerCase()
        .includes(value)
    );
  });
}, [petsResponse, search]);
  // ============================================================
  // FLATTEN CATEGORY TREE
  // ============================================================

  const flattenCategories = useCallback(
    (
      nodes: CategoryNode[],
      output: CategoryNode[] = [],
    ): CategoryNode[] => {
      for (const node of nodes) {
        if (
          node.isDeleted === true ||
          node.isActive === false
        ) {
          continue;
        }

        output.push(node);

        if (
          node.children &&
          node.children.length > 0
        ) {
          flattenCategories(
            node.children,
            output,
          );
        }
      }

      return output;
    },
    [],
  );

  const categories = useMemo(() => {
    if (
      normalizeCategoryResponse.length === 0
    ) {
      return [];
    }

    return flattenCategories(
      normalizeCategoryResponse,
    );
  }, [
    normalizeCategoryResponse,
    flattenCategories,
  ]);

  // ============================================================
  // CATEGORY SEARCH
  // ============================================================

  const filteredCategories =
    useMemo(() => {
      const value =
        search.trim().toLowerCase();

      if (!value) {
        return categories;
      }

      return categories.filter(
        (category) =>
          category.name
            .toLowerCase()
            .includes(value),
      );
    }, [categories, search]);

  // ============================================================
  // BRAND SEARCH
  // ============================================================

  const filteredBrands = useMemo(() => {
    const value =
      search.trim().toLowerCase();

    if (!value) {
      return brands;
    }

    return brands.filter((brand: any) =>
      String(brand.name || "")
        .toLowerCase()
        .includes(value),
    );
  }, [brands, search]);

  // ============================================================
  // PRODUCTS
  // ============================================================

  const filteredProducts = useMemo(() => {
    return productsResponse?.products || [];
  }, [productsResponse]);

  // ============================================================
  // VIDEOS
  // ============================================================

  const availableVideos =
    useMemo<VideoSuggestion[]>(() => {
      return (
        (videosResponse as
          | VideoSuggestionsResponse
          | undefined)?.items || []
      );
    }, [videosResponse]);

  // ============================================================
  // SELECTED CHECK
  // ============================================================

  const isSelected = (
    itemId: number,
  ) => {
    if (!contentType) {
      return false;
    }

    return selectedItems.some(
      (item) =>
        item.itemType === contentType &&
        item.itemId === itemId,
    );
  };

  const isVideoSelected = (
    itemId: number,
    itemType: VideoItemType,
  ) => {
    return selectedItems.some(
      (item) =>
        item.itemType === itemType &&
        item.itemId === itemId,
    );
  };

  // ============================================================
  // ADD STANDARD ITEM
  // ============================================================

  const addItem = (item: {
    id: number;
    name: string;
    imageUrl?: string | null;
    itemType?: ContentType;
  }) => {
    if (!contentType) {
      return;
    }

    if (contentType === "Custom") {
      return;
    }

    if (isDynamicSection) {
      return;
    }

    const actualItemType: ContentType =
      item.itemType ||
      (contentType === "Video"
        ? section?.sectionType ===
          "VideoCarouselPets"
          ? "Pet"
          : "Product"
        : contentType);

    const alreadySelected =
      selectedItems.some(
        (selected) =>
          selected.itemType ===
            actualItemType &&
          selected.itemId === item.id,
      );

    if (alreadySelected) {
      return;
    }

    setSelectedItems((previous) => [
      ...previous,
      {
        clientKey:
          crypto.randomUUID(),

        id: 0,

        itemType: actualItemType,

        itemId: item.id,

        name: item.name,

        imageUrl:
          item.imageUrl || null,

        displayOrder:
          previous.length + 1,

        isActive: true,

        title: null,

        redirectUrl: null,

        configJson: null,
      },
    ]);
  };

  // ============================================================
  // ADD CUSTOM ITEM
  // ============================================================

  const addCustomItem = () => {
    if (contentType !== "Custom") {
      return;
    }

    if (!customForm.imageUrl.trim()) {
      addToast(
        "error",
        "Validation Error",
        "Please enter an image URL.",
      );

      return;
    }

    let configJson: string | null =
      customForm.configJson.trim() || null;

    if (customForm.configJson.trim()) {
      try {
        JSON.parse(
          customForm.configJson,
        );
      } catch {
        addToast(
          "error",
          "Validation Error",
          "Config JSON is invalid.",
        );

        return;
      }
    }

    const title =
      customForm.title.trim();

    const newItem: SelectedItem = {
      clientKey:
        crypto.randomUUID(),

      id: 0,

      itemType: "Custom",

      itemId: null,

      name:
        title || "Custom Content",

      title:
        title || null,

      imageUrl:
        customForm.imageUrl.trim(),

      redirectUrl:
        customForm.redirectUrl.trim() ||
        null,

      configJson,

      displayOrder:
        selectedItems.length + 1,

      isActive: true,
    };

    setSelectedItems((previous) => [
      ...previous,
      {
        ...newItem,
        displayOrder:
          previous.length + 1,
      },
    ]);

    resetCustomForm();
  };

  // ============================================================
  // RESET CUSTOM FORM
  // ============================================================

  const resetCustomForm = () => {
    setEditingCustomKey(null);

    setCustomForm({
      title: "",
      imageUrl: "",
      redirectUrl: "",
      configJson: "",
    });
  };

  // ============================================================
  // EDIT CUSTOM ITEM
  // ============================================================

  const editCustomItem = (
    item: SelectedItem,
  ) => {
    if (item.itemType !== "Custom") {
      return;
    }

    setEditingCustomKey(
      item.clientKey,
    );

    setCustomForm({
      title:
        item.title ||
        item.name ||
        "",

      imageUrl:
        item.imageUrl || "",

      redirectUrl:
        item.redirectUrl || "",

      configJson:
        item.configJson || "",
    });
  };

  // ============================================================
  // UPDATE CUSTOM ITEM
  // ============================================================

  const updateCustomItem = () => {
    if (!editingCustomKey) {
      return;
    }

    if (contentType !== "Custom") {
      return;
    }

    if (!customForm.imageUrl.trim()) {
      addToast(
        "error",
        "Validation Error",
        "Please enter an image URL.",
      );

      return;
    }

    if (customForm.configJson.trim()) {
      try {
        JSON.parse(
          customForm.configJson,
        );
      } catch {
        addToast(
          "error",
          "Validation Error",
          "Config JSON is invalid.",
        );

        return;
      }
    }

    setSelectedItems((previous) =>
      previous.map((item) =>
        item.clientKey ===
        editingCustomKey
          ? {
              ...item,

              name:
                customForm.title.trim() ||
                "Custom Content",

              title:
                customForm.title.trim() ||
                null,

              imageUrl:
                customForm.imageUrl.trim(),

              redirectUrl:
                customForm.redirectUrl.trim() ||
                null,

              configJson:
                customForm.configJson.trim() ||
                null,
            }
          : item,
      ),
    );

    resetCustomForm();
  };

  // ============================================================
  // REMOVE ITEM
  // ============================================================

  const removeItem = (
    clientKey: string,
  ) => {
    if (isDynamicSection) {
      return;
    }

    setSelectedItems((previous) =>
      previous
        .filter(
          (item) =>
            item.clientKey !==
            clientKey,
        )
        .map((item, index) => ({
          ...item,
          displayOrder:
            index + 1,
        })),
    );
  };

  // ============================================================
  // TOGGLE ACTIVE / INACTIVE
  // ============================================================

  const toggleItemActive = (
    clientKey: string,
  ) => {
    if (isDynamicSection) {
      return;
    }

    setSelectedItems((previous) =>
      previous.map((item) =>
        item.clientKey === clientKey
          ? {
              ...item,
              isActive:
                !item.isActive,
            }
          : item,
      ),
    );
  };

  // ============================================================
  // DRAG START
  // ============================================================

  const handleDragStart = (
    index: number,
  ) => {
    if (isDynamicSection) {
      return;
    }

    setDraggedIndex(index);
  };

  // ============================================================
  // DRAG OVER
  // ============================================================

  const handleDragOver = (
    event: React.DragEvent,
    targetIndex: number,
  ) => {
    event.preventDefault();

    if (
      isDynamicSection ||
      draggedIndex === null ||
      draggedIndex === targetIndex
    ) {
      return;
    }

    setSelectedItems((previous) => {
      const result = [...previous];

      const [moved] =
        result.splice(
          draggedIndex,
          1,
        );

      result.splice(
        targetIndex,
        0,
        moved,
      );

      return result.map(
        (item, index) => ({
          ...item,
          displayOrder:
            index + 1,
        }),
      );
    });

    setDraggedIndex(targetIndex);
  };

  // ============================================================
  // DRAG END
  // ============================================================

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // ============================================================
  // SAVE
  // ============================================================

  const handleSave = async () => {
    if (!contentType) {
      addToast(
        "error",
        "Save Failed",
        "This section does not support content management.",
      );

      return;
    }

    if (isDynamicSection) {
      return;
    }

    const payload = {
      items: selectedItems.map(
        (item, index) => ({
          id:
            item.id > 0
              ? item.id
              : null,

          itemType:
            item.itemType,

          itemId:
            item.itemId,

          displayOrder:
            index + 1,

          isActive:
            item.isActive,

          title:
            item.title || null,

          imageUrl:
            item.imageUrl || null,

          redirectUrl:
            item.redirectUrl || null,

          configJson:
            item.configJson || null,
        }),
      ),
    };

    try {
      await saveSectionItems({
        sectionId: id,
        data: payload,
      }).unwrap();

      await loadExisting();

      addToast(
        "success",
        "Saved",
        "Section items saved successfully.",
      );
    } catch (error) {
      console.error(
        "Failed to save section items",
        error,
      );

      addToast(
        "error",
        "Save Failed",
        "Failed to save section items.",
      );
    }
  };

  // ============================================================
  // RENDER PRODUCTS
  // ============================================================

  const renderProducts = () => {
    if (filteredProducts.length === 0) {
      return (
        <EmptyState text="No products found." />
      );
    }

    return (
      <div className="space-y-2">
        {filteredProducts.map(
          (product: any) => {
            const productId =
              product.productId;

            const selected =
              isSelected(productId);

            const name =
              product.productName ||
              product.name ||
              `Product #${productId}`;

            const image =
              product.thumbnail ||
              product.imageUrl ||
              null;

            return (
              <button
                key={productId}
                type="button"
                disabled={selected}
                onClick={() =>
                  addItem({
                    id: productId,
                    name,
                    imageUrl: image,
                  })
                }
                className={`
                  w-full
                  flex
                  items-center
                  gap-3
                  border
                  rounded-xl
                  p-3
                  text-left
                  transition
                  ${
                    selected
                      ? "bg-slate-50 opacity-50"
                      : "hover:border-slate-400"
                  }
                `}
              >
                {image ? (
                  <img
                    src={image}
                    className="w-12 h-12 rounded-lg object-contain bg-slate-50"
                    alt=""
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-100" />
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {name}
                  </p>

                  <p className="text-xs text-slate-400">
                    #{productId}
                  </p>
                </div>

                {selected && (
                  <Check
                    size={18}
                    className="text-green-600"
                  />
                )}
              </button>
            );
          },
        )}
      </div>
    );
  };

  // ============================================================
// RENDER PETS
// ============================================================

const renderPets = () => {
  if (filteredPets.length === 0) {
    return (
      <EmptyState
        text={
          search
            ? "No pets found for this search."
            : "No pets found."
        }
      />
    );
  }

  return (
    <div className="space-y-2">
      {filteredPets.map((pet: any) => {
        const petId = Number(
          pet.petId ?? pet.id
        );

        const selected = isSelected(petId);

        const name =
          pet.petName ||
          pet.name ||
          `Pet #${petId}`;

        const image =
          pet.productImage ||
          pet.thumbnailUrl ||
          pet.imageUrl ||
          null;

        return (
          <button
            key={petId}
            type="button"
            disabled={selected}
            onClick={() =>
              addItem({
                id: petId,
                name,
                imageUrl: image,
                itemType: "Pet",
              })
            }
            className={`
              w-full
              flex
              items-center
              gap-3
              border
              rounded-xl
              p-3
              text-left
              transition
              ${
                selected
                  ? "bg-slate-50 opacity-50"
                  : "hover:border-slate-400"
              }
            `}
          >
            {image ? (
              <img
                src={image}
                className="w-14 h-14 rounded-lg object-cover bg-slate-50 shrink-0"
                alt={name}
              />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-slate-100 shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">
                  {name}
                </p>

                {pet.gender && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">
                    {pet.gender}
                  </span>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-0.5">
                {pet.breed || "Pet"} • #{petId}
              </p>

              {pet.price !== undefined && (
                <p className="text-xs text-slate-500 mt-1">
                  AED {Number(
                    pet.salePrice ??
                    pet.price ??
                    0
                  ).toFixed(2)}
                </p>
              )}
            </div>

            {selected && (
              <Check
                size={18}
                className="text-green-600 shrink-0"
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

  // ============================================================
  // RENDER CATEGORIES
  // ============================================================

  const renderCategories = () => {
    if (
      filteredCategories.length ===
      0
    ) {
      return (
        <EmptyState
          text={
            search
              ? "No categories found for this search."
              : "No categories found."
          }
        />
      );
    }

    return (
      <div className="space-y-2">
        {filteredCategories.map(
          (category) => {
            const selected =
              isSelected(category.id);

            const level =
              category.level || 0;

            return (
              <button
                key={category.id}
                type="button"
                disabled={selected}
                onClick={() =>
                  addItem({
                    id: category.id,
                    name: category.name,
                    imageUrl:
                      category.imageUrl,
                  })
                }
                className={`
                  w-full
                  flex
                  items-center
                  gap-3
                  border
                  rounded-xl
                  p-3
                  text-left
                  transition
                  ${
                    selected
                      ? "bg-slate-50 opacity-50"
                      : "hover:border-slate-400"
                  }
                `}
              >
                <div
                  className="flex items-center gap-3 flex-1"
                  style={{
                    marginLeft:
                      `${level * 18}px`,
                  }}
                >
                  {category.imageUrl ? (
                    <img
                      src={
                        category.imageUrl
                      }
                      className="w-10 h-10 rounded-lg object-cover"
                      alt=""
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-slate-100" />
                  )}

                  <div>
                    <p className="text-sm font-medium">
                      {category.name}
                    </p>

                    <p className="text-[11px] text-slate-400">
                      Category #
                      {category.id}
                    </p>
                  </div>
                </div>

                {selected && (
                  <Check
                    size={18}
                    className="text-green-600"
                  />
                )}
              </button>
            );
          },
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER BRANDS
  // ============================================================

  const renderBrands = () => {
    if (
      filteredBrands.length ===
      0
    ) {
      return (
        <EmptyState
          text={
            search
              ? "No brands found for this search."
              : "No brands found."
          }
        />
      );
    }

    return (
      <div className="space-y-2">
        {filteredBrands.map(
          (brand: any) => {
            const selected =
              isSelected(brand.id);

            return (
              <button
                key={brand.id}
                type="button"
                disabled={selected}
                onClick={() =>
                  addItem({
                    id: brand.id,
                    name: brand.name,
                    imageUrl:
                      brand.logoUrl,
                  })
                }
                className={`
                  w-full
                  flex
                  items-center
                  gap-3
                  border
                  rounded-xl
                  p-3
                  text-left
                  transition
                  ${
                    selected
                      ? "bg-slate-50 opacity-50"
                      : "hover:border-slate-400"
                  }
                `}
              >
                {brand.logoUrl ? (
                  <img
                    src={brand.logoUrl}
                    className="w-12 h-12 rounded-lg object-contain bg-slate-50"
                    alt=""
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-slate-100" />
                )}

                <div className="flex-1">
                  <p className="text-sm font-medium">
                    {brand.name}
                  </p>

                  <p className="text-xs text-slate-400">
                    Brand #{brand.id}
                  </p>
                </div>

                {selected && (
                  <Check
                    size={18}
                    className="text-green-600"
                  />
                )}
              </button>
            );
          },
        )}
      </div>
    );
  };

  // ============================================================
  // RENDER VIDEOS
  // ============================================================

  const renderVideos = () => {
    if (
      availableVideos.length ===
      0
    ) {
      return (
        <EmptyState text="No videos found." />
      );
    }

    return (
      <div className="space-y-2">
        {availableVideos.map(
          (video) => {
            const selected =
              isVideoSelected(
                video.itemId,
                video.itemType,
              );

            const previewImage =
              video.thumbnailUrl ||
              video.imageUrl ||
              null;

            return (
              <button
                key={`${video.itemType}-${video.itemId}`}
                type="button"
                disabled={selected}
                onClick={() =>
                  addItem({
                    id: video.itemId,
                    name: video.name,
                    imageUrl:
                      previewImage,
                    itemType:
                      video.itemType,
                  })
                }
                className={`
                  w-full
                  flex
                  items-center
                  gap-3
                  border
                  rounded-xl
                  p-3
                  text-left
                  transition
                  ${
                    selected
                      ? "bg-slate-50 opacity-50"
                      : "hover:border-slate-400"
                  }
                `}
              >
                {previewImage ? (
                  <img
                    src={previewImage}
                    className="w-20 h-12 rounded-lg object-cover bg-slate-50 shrink-0"
                    alt=""
                  />
                ) : (
                  <div className="w-20 h-12 rounded-lg bg-slate-100 shrink-0" />
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">
                      {video.name}
                    </p>

                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">
                      {video.itemType}
                    </span>
                  </div>

                  <p className="text-xs text-slate-400 mt-0.5">
                    #{video.itemId}
                  </p>

                  {video.videoUrl && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">
                      {video.videoUrl}
                    </p>
                  )}
                </div>

                {selected && (
                  <Check
                    size={18}
                    className="text-green-600 shrink-0"
                  />
                )}
              </button>
            );
          },
        )}
      </div>
    );
  };

  // ============================================================
  // CUSTOM FORM
  // ============================================================

  const renderCustom = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Image URL *
          </label>

          <input
            value={
              customForm.imageUrl
            }
            onChange={(e) =>
              setCustomForm(
                (previous) => ({
                  ...previous,
                  imageUrl:
                    e.target.value,
                }),
              )
            }
            placeholder="https://..."
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-500"
          />
        </div>

        {customForm.imageUrl && (
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
            <img
              src={
                customForm.imageUrl
              }
              alt=""
              className="w-full h-40 object-contain"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Title
          </label>

          <input
            value={
              customForm.title
            }
            onChange={(e) =>
              setCustomForm(
                (previous) => ({
                  ...previous,
                  title:
                    e.target.value,
                }),
              )
            }
            placeholder="Optional title"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Redirect URL
          </label>

          <input
            value={
              customForm.redirectUrl
            }
            onChange={(e) =>
              setCustomForm(
                (previous) => ({
                  ...previous,
                  redirectUrl:
                    e.target.value,
                }),
              )
            }
            placeholder="/offers/summer-sale"
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm outline-none focus:border-slate-500"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">
            Config JSON
          </label>

          <textarea
            value={
              customForm.configJson
            }
            onChange={(e) =>
              setCustomForm(
                (previous) => ({
                  ...previous,
                  configJson:
                    e.target.value,
                }),
              )
            }
            rows={5}
            placeholder={`{
  "openInWebView": false
}`}
            className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:border-slate-500 resize-none"
          />
        </div>

        <button
          type="button"
          onClick={
            editingCustomKey
              ? updateCustomItem
              : addCustomItem
          }
          className="w-full flex items-center justify-center gap-2 bg-slate-900 text-white rounded-xl px-4 py-3 text-sm font-medium hover:bg-slate-800"
        >
          {editingCustomKey ? (
            <>
              <Check size={16} />
              Update Content
            </>
          ) : (
            <>
              <Plus size={16} />
              Add Content
            </>
          )}
        </button>

        {editingCustomKey && (
          <button
            type="button"
            onClick={
              resetCustomForm
            }
            className="w-full text-sm text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
        )}
      </div>
    );
  };

  // ============================================================
  // DYNAMIC INFORMATION
  // ============================================================

  const renderDynamicInfo = () => {
    return (
      <div className="border border-blue-100 bg-blue-50 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
            <Info
              size={18}
              className="text-blue-600"
            />
          </div>

          <div>
            <h3 className="text-sm font-semibold text-blue-900">
              Dynamic Content
            </h3>

            <p className="text-sm text-blue-700 mt-1 leading-relaxed">
              This section is automatically
              populated from the database.
              Content cannot be added or
              reordered manually here.
            </p>

            <p className="text-xs text-blue-600 mt-2">
              Edit the section configuration
              to change how this content is
              generated.
            </p>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================
  // SOURCE
  // ============================================================

  const renderSource = () => {
    if (isDynamicSection) {
      return renderDynamicInfo();
    }

    switch (contentType) {
      case "Product":
        return renderProducts();

      case "Category":
        return renderCategories();

      case "Brand":
        return renderBrands();

      case "Custom":
        return renderCustom();

      case "Pet":
         return renderPets();

      case "Service":
        return (
          <EmptyState text="Service picker is not configured yet." />
        );

      case "Video":
        return renderVideos();

      default:
        return (
          <EmptyState text="Select a content type." />
        );
    }
  };

  // ============================================================
  // LOADING
  // ============================================================

const isLoadingSource =
  !isDynamicSection &&
  (
    isLoadingProducts ||
    isFetchingProducts ||
    isLoadingCategories ||
    isFetchingCategories ||
    isLoadingBrands ||
    isFetchingBrands ||
    isLoadingPets ||
    isFetchingPets ||
    isLoadingVideos ||
    isFetchingVideos
  );

  // ============================================================
  // SCREEN
  // ============================================================

  return (
    <div className="w-full max-w-7xl mx-auto px-4 pb-12">
      <ToastContainer
        toasts={toasts}
        onClose={removeToast}
      />

      {/* ======================================================
          HEADER
      ====================================================== */}

      <button
        type="button"
        onClick={() =>
          navigate(
            "/admin/uiconfigure/sections",
            {
              state: {
                screenId:
                  state?.screenId ?? 1,
                screenName:
                  state?.screenName,
              },
            },
          )
        }
        className="flex items-center gap-2 text-sm text-slate-500 mb-6 hover:text-slate-800"
      >
        <ArrowLeft size={17} />
        Back to Sections
      </button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">
            Manage Content
          </h1>

          <p className="text-sm text-slate-500 mt-1">
            {section?.displayName ||
              section?.sectionName ||
              definition?.label ||
              section?.sectionType}
          </p>

          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="text-slate-400">
              Content Type:
            </span>

            <span className="font-medium text-slate-600">
              {contentType ||
                "Not configured"}
            </span>

            {contentType !== "Custom" && (
              <>
                <span className="text-slate-300">
                  |
                </span>

                <span className="text-slate-400">
                  Source:
                </span>

                <span
                  className={
                    sourceType ===
                    "Dynamic"
                      ? "font-medium text-blue-600"
                      : "font-medium text-emerald-600"
                  }
                >
                  {sourceType}
                </span>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          disabled={
            isSaving ||
            isDynamicSection
          }
          onClick={handleSave}
          className="bg-black text-white rounded-xl px-6 py-3 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isDynamicSection
            ? "Managed Automatically"
            : isSaving
              ? "Saving..."
              : "Save Changes"}
        </button>
      </div>

      {/* ======================================================
          DYNAMIC SECTION NOTICE
      ====================================================== */}

      {isDynamicSection && (
        <div className="mb-6 border border-blue-100 bg-blue-50 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <Info
              size={19}
              className="text-blue-600 mt-0.5 shrink-0"
            />

            <div>
              <h2 className="text-sm font-semibold text-blue-900">
                This section is dynamically
                populated
              </h2>

              <p className="text-sm text-blue-700 mt-1">
                The application loads this
                section's content automatically
                from the database. There is no
                manual content to manage here.
              </p>

              <p className="text-xs text-blue-600 mt-2">
                To change the source, item count
                or filtering rules, edit the
                section configuration.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          TWO COLUMN LAYOUT
      ====================================================== */}

      <div
        className={
          isDynamicSection
            ? "grid grid-cols-1"
            : "grid grid-cols-1 lg:grid-cols-2 gap-6"
        }
      >
        {/* ====================================================
            AVAILABLE / SOURCE
        ==================================================== */}

        <div className="border border-slate-200 rounded-2xl bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium">
                {isDynamicSection
                  ? "Content Source"
                  : contentType ===
                      "Custom"
                    ? "Custom Content"
                    : `Available ${
                        contentType ||
                        "Content"
                      }`}
              </h2>

              <p className="text-xs text-slate-400 mt-1">
                {isDynamicSection
                  ? "Content is generated automatically."
                  : contentType ===
                      "Custom"
                    ? "Create custom content for this section."
                    : "Select content to add to this section."}
              </p>
            </div>
          </div>

          {/* SEARCH */}

          {contentType &&
            contentType !== "Custom" &&
            contentType !== "Video" &&
            !isDynamicSection && (
              <div className="relative mb-5">
                <Search
                  size={17}
                  className="absolute left-3 top-3 text-slate-400"
                />

                <input
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value,
                    )
                  }
                  placeholder={`Search ${contentType.toLowerCase()}...`}
                  className="w-full border border-slate-300 rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:border-slate-500"
                />
              </div>
            )}

          {/* SOURCE */}

          {isLoadingSource &&
          !isDynamicSection &&
          contentType !== "Custom" ? (
            <div className="py-8 flex justify-center">
              <Loader2
                size={20}
                className="animate-spin"
              />
            </div>
          ) : (
            renderSource()
          )}
        </div>

        {/* ====================================================
            SELECTED CONTENT
        ==================================================== */}

        {!isDynamicSection && (
          <div className="border border-slate-200 rounded-2xl bg-white p-5">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-medium">
                  Selected Content
                </h2>

                <p className="text-xs text-slate-400 mt-1">
                  Drag items to change customer-facing order.
                </p>
              </div>

              <span className="text-sm text-slate-500">
                {selectedItems.length}
              </span>
            </div>

            {isLoadingItems ? (
              <div className="py-10 flex justify-center">
                <Loader2
                  size={20}
                  className="animate-spin"
                />
              </div>
            ) : selectedItems.length ===
              0 ? (
              <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
                No content selected.
              </div>
            ) : (
              <div className="space-y-3">
                {selectedItems.map(
                  (item, index) => (
                    <div
                      key={
                        item.clientKey
                      }
                      draggable={
                        !isDynamicSection
                      }
                      onDragStart={() =>
                        handleDragStart(
                          index,
                        )
                      }
                      onDragOver={(
                        event,
                      ) =>
                        handleDragOver(
                          event,
                          index,
                        )
                      }
                      onDragEnd={
                        handleDragEnd
                      }
                      className={`
                        border
                        rounded-xl
                        p-3
                        flex
                        items-center
                        gap-3
                        transition
                        ${
                          draggedIndex ===
                          index
                            ? "opacity-50 border-slate-400 bg-slate-50"
                            : "border-slate-200"
                        }
                      `}
                    >
                      <div className="shrink-0 text-slate-400 cursor-grab active:cursor-grabbing">
                        <GripVertical
                          size={18}
                        />
                      </div>

                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs shrink-0">
                        {index + 1}
                      </div>

                      {item.imageUrl ? (
                        <img
                          src={
                            item.imageUrl
                          }
                          className="w-12 h-12 rounded-lg object-contain bg-slate-50 shrink-0"
                          alt=""
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 shrink-0" />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">
                            {item.title ||
                              item.name}
                          </p>

                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 shrink-0">
                            {item.itemType}
                          </span>
                        </div>

                        {item.itemType ===
                        "Custom" ? (
                          <p className="text-xs text-slate-400 mt-0.5">
                            Custom content
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 mt-0.5">
                            #{item.itemId}
                          </p>
                        )}

                        {item.redirectUrl && (
                          <p className="text-[11px] text-slate-400 truncate mt-0.5">
                            {
                              item.redirectUrl
                            }
                          </p>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          toggleItemActive(
                            item.clientKey,
                          )
                        }
                        className={
                          item.isActive
                            ? "px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 shrink-0"
                            : "px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-500 shrink-0"
                        }
                      >
                        {item.isActive
                          ? "Active"
                          : "Inactive"}
                      </button>

                      {item.itemType ===
                        "Custom" && (
                        <button
                          type="button"
                          onClick={() =>
                            editCustomItem(
                              item,
                            )
                          }
                          className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 shrink-0"
                          
                          title="Edit custom content"
                        >
                          <Pencil
                            size={15}
                          />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() =>
                          removeItem(
                            item.clientKey,
                          )
                        }
                        className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-500 shrink-0"
                        title="Remove from section"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// EMPTY STATE
// ============================================================

const EmptyState: React.FC<{
  text: string;
}> = ({ text }) => {
  return (
    <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
      {text}
    </div>
  );
};

export default ManageSectionItemsScreen;